import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Mic, Target, Briefcase, CheckCircle, Plus, X, Loader2, 
  AlertTriangle, DollarSign, RotateCcw, Ban
} from "lucide-react";
import { API, authFetch } from "@/App";

const AVAILABLE_PROGRAMS = [
  { id: "podcast_academy", name: "Podcast Launch Academy", icon: Mic, color: "purple" },
  { id: "rapid", name: "R.A.P.I.D.™ Program", icon: Target, color: "blue" },
  { id: "premium_rapid", name: "R.A.P.I.D.™ Program", icon: Target, color: "blue" }, // Alias
];

export function ClientProgramsDialog({ 
  open, 
  onOpenChange, 
  client,
  onRefresh
}) {
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [downgradeReason, setDowngradeReason] = useState("");
  const [downgradingClient, setDowngradingClient] = useState(false);

  useEffect(() => {
    if (open && client?.user_id) {
      fetchClientPrograms();
    }
  }, [open, client?.user_id]);

  const fetchClientPrograms = async () => {
    if (!client?.user_id) return;
    setLoading(true);
    try {
      const response = await authFetch(`${API}/consultant/clients/${client.user_id}/programs`);
      if (response.ok) {
        const data = await response.json();
        setClientData(data);
        setPrograms(data.programs || []);
      }
    } catch (_error) {
      console.error("Error fetching programs:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgram = async (programId) => {
    setProcessing(programId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${client.user_id}/programs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "add", program: programId })
      });
      
      if (response.ok) {
        toast.success(`Program added to ${client.name}`);
        fetchClientPrograms();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add program");
      }
    } catch (_error) {
      toast.error("Failed to add program");
    } finally {
      setProcessing(null);
    }
  };

  const handleRemoveProgram = async (programId) => {
    if (!confirm(`Remove this program from ${client.name}?`)) return;
    
    setProcessing(programId);
    try {
      const response = await authFetch(`${API}/consultant/clients/${client.user_id}/programs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "remove", program: programId })
      });
      
      if (response.ok) {
        toast.success(`Program removed from ${client.name}`);
        fetchClientPrograms();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to remove program");
      }
    } catch (_error) {
      toast.error("Failed to remove program");
    } finally {
      setProcessing(null);
    }
  };

  const handleDowngrade = async () => {
    setDowngradingClient(true);
    try {
      const response = await authFetch(`${API}/consultant/clients/${client.user_id}/downgrade`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: downgradeReason || "Non-payment" })
      });
      
      if (response.ok) {
        toast.success(`${client.name} downgraded to Basic`);
        setShowDowngradeDialog(false);
        setDowngradeReason("");
        fetchClientPrograms();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to downgrade client");
      }
    } catch (_error) {
      toast.error("Failed to downgrade client");
    } finally {
      setDowngradingClient(false);
    }
  };

  const handleRestore = async () => {
    if (!confirm(`Restore ${client.name} to their previous tier?`)) return;
    
    setProcessing("restore");
    try {
      const response = await authFetch(`${API}/consultant/clients/${client.user_id}/restore`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `${client.name} restored!`);
        fetchClientPrograms();
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to restore client");
      }
    } catch (_error) {
      toast.error("Failed to restore client");
    } finally {
      setProcessing(null);
    }
  };

  const isProgramActive = (programId) => {
    if (programId === "podcast_academy") return clientData?.podcast_access;
    if (programId === "rapid" || programId === "premium_rapid") return clientData?.rapid_access;
    return programs.includes(programId);
  };

  const isDowngraded = client?.downgraded_at || client?.tier === "basic" && client?.previous_tier;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Manage Programs - {client?.name}
            </DialogTitle>
            <DialogDescription>
              Add or remove program access for this client. Programs run alongside their main consulting tier.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Current Tier Info */}
              <div className="bg-neutral-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-500">Current Tier</p>
                    <p className="font-semibold text-neutral-900">
                      {client?.tier === "basic" ? "Basic Consulting" :
                       client?.tier === "premium_rapid" ? "R.A.P.I.D.™ Program" :
                       client?.tier === "podcast_academy" ? "Podcast Launch Academy" :
                       client?.tier === "growth_strategic" ? "Strategic Foundation" :
                       client?.tier === "growth_optimization" ? "Growth & Optimization" :
                       client?.tier === "growth_scale" ? "Scale & Leadership" :
                       "Unassigned"}
                    </p>
                  </div>
                  {isDowngraded && (
                    <Badge className="bg-red-100 text-red-700">Downgraded</Badge>
                  )}
                </div>
                {client?.previous_tier && client?.downgraded_at && (
                  <p className="text-xs text-amber-600 mt-2">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Previously: {client.previous_tier} - Downgraded for: {client.downgrade_reason || "Non-payment"}
                  </p>
                )}
              </div>

              {/* Available Programs */}
              <div>
                <Label className="text-sm font-medium">Additional Programs</Label>
                <p className="text-xs text-neutral-500 mb-3">
                  Toggle programs to grant or revoke access
                </p>
                <div className="space-y-2">
                  {AVAILABLE_PROGRAMS.filter(p => p.id !== "premium_rapid").map((program) => {
                    const isActive = isProgramActive(program.id);
                    const Icon = program.icon;
                    
                    return (
                      <div 
                        key={program.id}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                          isActive 
                            ? `border-${program.color}-200 bg-${program.color}-50` 
                            : "border-neutral-200 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isActive 
                              ? `bg-${program.color}-100 text-${program.color}-600` 
                              : "bg-neutral-100 text-neutral-400"
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className={`font-medium ${isActive ? "text-neutral-900" : "text-neutral-600"}`}>
                              {program.name}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {isActive ? "Access granted" : "No access"}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant={isActive ? "outline" : "default"}
                          size="sm"
                          onClick={() => isActive ? handleRemoveProgram(program.id) : handleAddProgram(program.id)}
                          disabled={processing === program.id}
                          className={isActive 
                            ? "border-red-200 text-red-600 hover:bg-red-50" 
                            : "bg-green-600 hover:bg-green-700"
                          }
                          data-testid={`program-toggle-${program.id}`}
                        >
                          {processing === program.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isActive ? (
                            <><X className="w-4 h-4 mr-1" />Remove</>
                          ) : (
                            <><Plus className="w-4 h-4 mr-1" />Add</>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Account Actions */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Account Actions</Label>
                <div className="flex gap-2 mt-3">
                  {isDowngraded ? (
                    <Button
                      variant="outline"
                      className="flex-1 border-green-200 text-green-700 hover:bg-green-50"
                      onClick={handleRestore}
                      disabled={processing === "restore"}
                      data-testid="restore-client-btn"
                    >
                      {processing === "restore" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-2" />
                      )}
                      Restore Account
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => setShowDowngradeDialog(true)}
                      data-testid="downgrade-client-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Downgrade (Non-Payment)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={showDowngradeDialog} onOpenChange={setShowDowngradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Downgrade Client
            </DialogTitle>
            <DialogDescription>
              This will revoke {client?.name}'s premium access and move them to Basic tier. 
              Their previous tier will be saved for easy restoration.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for Downgrade</Label>
            <Textarea
              value={downgradeReason}
              onChange={(e) => setDowngradeReason(e.target.value)}
              placeholder="e.g., Non-payment, subscription cancelled, etc."
              rows={3}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDowngradeDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDowngrade}
              disabled={downgradingClient}
              data-testid="confirm-downgrade-btn"
            >
              {downgradingClient ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Ban className="w-4 h-4 mr-2" />
              )}
              Confirm Downgrade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
